/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Portal from '@material-ui/core/Portal';
import Snackbar, {SnackbarCloseReason} from '@material-ui/core/Snackbar';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isString from 'lodash.isstring';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {useMedia} from '../hooks/useMedia';
import {NotificationGeneralAlertIcon} from '../icons/NotificationGeneralAlertIcon';
import {dlsColor, dlsZIndex} from '../theme/theme';

interface StyleProps {
  maxWidth?: string;
  zIndex?: number;
};

// defines the prop definitions for the session timeout ...
export type UserNotificationProps = {
  show: boolean;
  message: string;
  type?: string;
  actionLabel?: string;
  isIcon?: boolean;
  customAction?: boolean;
  customActionLabel?: string;
  customDismissLabel?: string;
  onDismiss?: () => void;
  onAction?: () => void;
  onClose?: () => void;
  autoHideDuration?: number;
  disableWindowBlurListener?: boolean;
  disableClickAwayListener?: boolean;
  partialSuccessState?: string | null;
} & StyleProps;

// defines the styles used by this component ...
const useStyles = makeStyles(theme => ({
  snackbarRoot : {
    display                        : 'flex',
    justifyContent                 : 'center',
    alignItems                     : 'center',
    width                          : '100%',
    zIndex                         : ({zIndex}: StyleProps) => zIndex ?? dlsZIndex('ionSnackbarRoot'),
    [theme.breakpoints.down('sm')] : {
      width : '97%'
    },
    '& .MuiSnackbarContent-message' : {
      height                       : 'fit-content !important',
      [theme.breakpoints.up('lg')] : {
        maxWidth : ({maxWidth}: StyleProps) => maxWidth
      }
    }
  },
  icon : {
    display       : 'inline-block',
    verticalAlign : 'middle',
    paddingTop    : 4,
    marginRight   : 24
  },
  snackbarMessageWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  action : {
    paddingLeft        : '0',
    '& [class*=label]' : {
      fontSize : '14px'
    }
  },
  snackbarPartialMessageWrapper : {
    fontSize : '12px'
  }
}));

/**
 * The IonSnackbar component provides the default notification component within
 * the DLS which is to be used to display messages of interest to users at the bottom
 * of the page. The IonSnackbar is typically used for editors to inform users of an
 * error when performing a crud operation, such as editing an entity, etc..
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 * ```typescript
 *   import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
 *
 *   export const SessionTimeout = ({show, onLogin}: SessionTimeoutProps) => {
 *     return (
 *       <IonSnackbar show={show}
 *         icon={<NotificationDisconnectIcon />}
 *         title={translate('notifications:session.title')}
 *         message={translate('notifications:session.message')}
 *         actionLabel={translate('notifications:session.login')}
 *         onAction={onLogin}
 *         onClose={onClose} />
 *     );
 *   };
 *
 * ```
 *
 */
export const IonSnackbar = ({
  show,
  message,
  type,
  actionLabel,
  customDismissLabel,
  onDismiss,
  onAction,
  onClose,
  maxWidth,
  zIndex,
  isIcon = false,
  autoHideDuration,
  disableWindowBlurListener,
  disableClickAwayListener = false,
  partialSuccessState = null
}: UserNotificationProps) => {
  const classes                 = useStyles({maxWidth, zIndex});
  const showIcon                = partialSuccessState?.indexOf('/') > -1 || type === 'error';
  const {isPhone}               = useMedia();
  const applyPartialStateStyles = isPhone && partialSuccessState;
  if (show) {
    return (
      <Portal>
        <Snackbar open
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          className={classes.snackbarRoot}
          action={
            (customDismissLabel || actionLabel) &&
            <>
              {customDismissLabel &&
                <TertiaryButton onClick={onDismiss} isAlt>{customDismissLabel}</TertiaryButton>}
              {actionLabel && <TertiaryButton usecaactive onClick={onAction} isAlt>{actionLabel}</TertiaryButton>}
            </>
          }
          message={
            <div className={classnames(classes.snackbarMessageWrapper,
              applyPartialStateStyles ? classes.snackbarPartialMessageWrapper : undefined)}>
              <div style={{display: isIcon || showIcon ? 'initial' : 'none'}} className={classes.icon}>
                <NotificationGeneralAlertIcon color={dlsColor('white')} />
              </div>
              {partialSuccessState && <span>{partialSuccessState}&nbsp;</span>}
              {!isString(message) ? <span>{message}</span> : message}
            </div>
          }
          ClickAwayListenerProps={disableClickAwayListener ? {
            onClickAway : function(e) {
              e.preventDefault();
              e.stopPropagation();
            }
          } : null}
          disableWindowBlurListener={disableWindowBlurListener}
          autoHideDuration={autoHideDuration || null}
          onClose={onClose}
          ContentProps={{
            classes : {
              action : applyPartialStateStyles ? classes.action : undefined
            }
          }} />
      </Portal>
    );
  }
  return null;
};
export default IonSnackbar;
